import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function ThirdPartyIntegrations() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("apps");
  const [loading, setLoading] = useState(false);
  
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appLogs, setAppLogs] = useState([]);
  const [appStats, setAppStats] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [permissions, setPermissions] = useState([]);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    appName: "",
    description: "",
    webhookUrl: "",
    permissions: ["read"],
    rateLimitPerMinute: 60,
    rateLimitPerDay: 10000,
    ipWhitelist: "",
    webhookEvents: []
  });

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const res = await api.get("/Auth/me");
      if (!res.data.isAdmin) {
        navigate("/admin");
        return;
      }
      loadApps();
      loadWebhookEvents();
      loadPermissions();
    } catch (err) {
      navigate("/login");
    }
  };

  const loadApps = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Integration/apps");
      setApps(res.data);
    } catch (err) {
      console.error("Error loading apps:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadWebhookEvents = async () => {
    try {
      const res = await api.get("/Integration/webhook-events");
      setWebhookEvents(res.data);
    } catch (err) {
      console.error("Error loading webhook events:", err);
    }
  };

  const loadPermissions = async () => {
    try {
      const res = await api.get("/Integration/permissions");
      setPermissions(res.data);
    } catch (err) {
      console.error("Error loading permissions:", err);
    }
  };

  const loadAppDetails = async (appId) => {
    setLoading(true);
    try {
      const [appRes, statsRes, logsRes] = await Promise.all([
        api.get(`/Integration/apps/${appId}`),
        api.get(`/Integration/apps/${appId}/stats`),
        api.get(`/Integration/apps/${appId}/logs?limit=50`)
      ]);
      setSelectedApp(appRes.data);
      setAppStats(statsRes.data);
      setAppLogs(logsRes.data);
      setShowDetailsModal(true);
    } catch (err) {
      console.error("Error loading app details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApp = async (e) => {
    e.preventDefault();
    if (!createForm.appName) {
      alert("Please enter an app name");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/Integration/apps", createForm);
      setNewCredentials({
        apiKey: res.data.app.apiKey,
        apiSecret: res.data.app.apiSecret,
        webhookSecret: res.data.app.webhookSecret
      });
      setShowCreateModal(false);
      setShowCredentialsModal(true);
      setCreateForm({
        appName: "",
        description: "",
        webhookUrl: "",
        permissions: ["read"],
        rateLimitPerMinute: 60,
        rateLimitPerDay: 10000,
        ipWhitelist: "",
        webhookEvents: []
      });
      loadApps();
    } catch (err) {
      alert("Error creating app: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateKeys = async (appId) => {
    if (!window.confirm("Are you sure you want to regenerate API keys? This will invalidate the current keys.")) {
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(`/Integration/apps/${appId}/regenerate-keys`, {
        regenerateApiKey: false,
        regenerateApiSecret: true,
        regenerateWebhookSecret: true
      });
      setNewCredentials(res.data.credentials);
      setShowCredentialsModal(true);
    } catch (err) {
      alert("Error regenerating keys: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeApp = async (appId, appName) => {
    if (!window.confirm(`Are you sure you want to revoke "${appName}"? This will disable all API access.`)) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/Integration/apps/${appId}`);
      alert("Application revoked successfully");
      loadApps();
      setShowDetailsModal(false);
    } catch (err) {
      alert("Error revoking app: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTestWebhook = async (appId, eventType) => {
    setLoading(true);
    try {
      await api.post(`/Integration/apps/${appId}/webhooks/test`, {
        eventType: eventType || "transaction.created",
        testPayload: { test: true, message: "Test webhook from Uptech" }
      });
      alert("Test webhook sent successfully!");
    } catch (err) {
      alert("Webhook test failed: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (perm) => {
    const current = createForm.permissions;
    if (current.includes(perm)) {
      setCreateForm({ ...createForm, permissions: current.filter(p => p !== perm) });
    } else {
      setCreateForm({ ...createForm, permissions: [...current, perm] });
    }
  };

  const toggleWebhookEvent = (event) => {
    const current = createForm.webhookEvents;
    if (current.includes(event)) {
      setCreateForm({ ...createForm, webhookEvents: current.filter(e => e !== event) });
    } else {
      setCreateForm({ ...createForm, webhookEvents: [...current, event] });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const formatDate = (date) => new Date(date).toLocaleString("en-NG", { 
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" 
  });

  const styles = {
    container: { padding: "20px", maxWidth: "1400px", margin: "0 auto", fontFamily: "Arial, sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
    title: { fontSize: "24px", fontWeight: "bold", color: "#1a1a2e" },
    backBtn: { padding: "10px 20px", background: "#6c757d", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
    createBtn: { padding: "12px 24px", background: "#16a34a", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold" },
    card: { background: "white", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    appGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: "20px" },
    appCard: { background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: "1px solid #e5e7eb" },
    appHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px" },
    appName: { fontSize: "18px", fontWeight: "bold", color: "#1a1a2e" },
    badge: { padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "500" },
    activeBadge: { background: "#dcfce7", color: "#16a34a" },
    revokedBadge: { background: "#fee2e2", color: "#dc2626" },
    apiKey: { fontFamily: "monospace", fontSize: "12px", background: "#f3f4f6", padding: "8px 12px", borderRadius: "6px", wordBreak: "break-all" },
    statsRow: { display: "flex", gap: "20px", marginTop: "15px" },
    stat: { flex: 1, textAlign: "center", padding: "10px", background: "#f9fafb", borderRadius: "8px" },
    statValue: { fontSize: "20px", fontWeight: "bold", color: "#1a1a2e" },
    statLabel: { fontSize: "12px", color: "#6b7280" },
    btnRow: { display: "flex", gap: "10px", marginTop: "15px" },
    btn: { flex: 1, padding: "10px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" },
    primaryBtn: { background: "#3b82f6", color: "white" },
    dangerBtn: { background: "#dc2626", color: "white" },
    secondaryBtn: { background: "#e5e7eb", color: "#374151" },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modal: { background: "white", borderRadius: "12px", padding: "25px", width: "600px", maxWidth: "95%", maxHeight: "90vh", overflow: "auto" },
    modalLarge: { width: "900px" },
    modalTitle: { fontSize: "20px", fontWeight: "bold", marginBottom: "20px", color: "#1a1a2e" },
    formGroup: { marginBottom: "15px" },
    label: { display: "block", fontWeight: "600", marginBottom: "5px", color: "#374151" },
    input: { width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
    textarea: { width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", minHeight: "80px", boxSizing: "border-box" },
    checkboxGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "10px" },
    checkbox: { display: "flex", alignItems: "center", gap: "8px", padding: "8px", background: "#f9fafb", borderRadius: "6px", cursor: "pointer" },
    checkedCheckbox: { background: "#dbeafe", border: "1px solid #3b82f6" },
    modalFooter: { display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" },
    credentialBox: { background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: "8px", padding: "15px", marginBottom: "15px" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
    th: { padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: "600", color: "#374151", background: "#f9fafb" },
    td: { padding: "10px 8px", borderBottom: "1px solid #e5e7eb" }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🔗 Third-Party Integrations</h1>
        <div style={{ display: "flex", gap: "10px" }}>
          <button style={styles.createBtn} onClick={() => setShowCreateModal(true)}>
            + Create New App
          </button>
          <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Back to Admin</button>
        </div>
      </div>

      {/* Info Card */}
      <div style={{ ...styles.card, background: "#eff6ff", border: "1px solid #3b82f6" }}>
        <strong>🔐 API Integration</strong>
        <p style={{ margin: "10px 0 0", color: "#1e40af" }}>
          Create API keys for third-party applications to integrate with your platform. 
          Each app gets unique credentials and can subscribe to webhook events.
        </p>
      </div>

      {/* Apps Grid */}
      {loading ? (
        <p>Loading...</p>
      ) : apps.length > 0 ? (
        <div style={styles.appGrid}>
          {apps.map(app => (
            <div key={app.id} style={styles.appCard}>
              <div style={styles.appHeader}>
                <div>
                  <div style={styles.appName}>{app.appName}</div>
                  <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>{app.description || "No description"}</div>
                </div>
                <span style={{ ...styles.badge, ...(app.status === "Active" ? styles.activeBadge : styles.revokedBadge) }}>
                  {app.status}
                </span>
              </div>
              
              <div style={{ marginBottom: "10px" }}>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>API Key:</div>
                <div style={styles.apiKey}>{app.apiKey}</div>
              </div>

              <div style={{ marginBottom: "10px" }}>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Permissions:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                  {app.permissions.map(p => (
                    <span key={p} style={{ ...styles.badge, background: "#e0e7ff", color: "#4f46e5" }}>{p}</span>
                  ))}
                </div>
              </div>

              <div style={styles.statsRow}>
                <div style={styles.stat}>
                  <div style={styles.statValue}>{app.totalRequests.toLocaleString()}</div>
                  <div style={styles.statLabel}>Total Requests</div>
                </div>
                <div style={styles.stat}>
                  <div style={{ ...styles.statValue, color: "#16a34a" }}>{app.successfulRequests.toLocaleString()}</div>
                  <div style={styles.statLabel}>Successful</div>
                </div>
                <div style={styles.stat}>
                  <div style={{ ...styles.statValue, color: "#dc2626" }}>{app.failedRequests.toLocaleString()}</div>
                  <div style={styles.statLabel}>Failed</div>
                </div>
              </div>

              <div style={styles.btnRow}>
                <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={() => loadAppDetails(app.id)}>
                  View Details
                </button>
                <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => copyToClipboard(app.apiKey)}>
                  Copy Key
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...styles.card, textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>🔗</div>
          <h3>No integrations yet</h3>
          <p style={{ color: "#6b7280" }}>Create your first third-party app to get started</p>
          <button style={{ ...styles.createBtn, marginTop: "20px" }} onClick={() => setShowCreateModal(true)}>
            + Create New App
          </button>
        </div>
      )}

      {/* Create App Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, ...styles.modalLarge }}>
            <h3 style={styles.modalTitle}>🆕 Create New Integration</h3>
            <form onSubmit={handleCreateApp}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>App Name *</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="My App"
                    value={createForm.appName}
                    onChange={(e) => setCreateForm({ ...createForm, appName: e.target.value })}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Webhook URL (optional)</label>
                  <input
                    type="url"
                    style={styles.input}
                    placeholder="https://yourapp.com/webhook"
                    value={createForm.webhookUrl}
                    onChange={(e) => setCreateForm({ ...createForm, webhookUrl: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  style={styles.textarea}
                  placeholder="What is this integration for?"
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Permissions</label>
                <div style={styles.checkboxGrid}>
                  {permissions.map(p => (
                    <label 
                      key={p.name} 
                      style={{ ...styles.checkbox, ...(createForm.permissions.includes(p.name) ? styles.checkedCheckbox : {}) }}
                    >
                      <input
                        type="checkbox"
                        checked={createForm.permissions.includes(p.name)}
                        onChange={() => togglePermission(p.name)}
                      />
                      <div>
                        <div style={{ fontWeight: "500" }}>{p.name}</div>
                        <div style={{ fontSize: "11px", color: "#6b7280" }}>{p.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {createForm.webhookUrl && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Webhook Events</label>
                  <div style={styles.checkboxGrid}>
                    {webhookEvents.map(event => (
                      <label 
                        key={event} 
                        style={{ ...styles.checkbox, ...(createForm.webhookEvents.includes(event) ? styles.checkedCheckbox : {}) }}
                      >
                        <input
                          type="checkbox"
                          checked={createForm.webhookEvents.includes(event)}
                          onChange={() => toggleWebhookEvent(event)}
                        />
                        <span style={{ fontSize: "12px" }}>{event}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px" }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Rate Limit (per minute)</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={createForm.rateLimitPerMinute}
                    onChange={(e) => setCreateForm({ ...createForm, rateLimitPerMinute: parseInt(e.target.value) })}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Rate Limit (per day)</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={createForm.rateLimitPerDay}
                    onChange={(e) => setCreateForm({ ...createForm, rateLimitPerDay: parseInt(e.target.value) })}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>IP Whitelist (comma-separated)</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="* for all IPs"
                    value={createForm.ipWhitelist}
                    onChange={(e) => setCreateForm({ ...createForm, ipWhitelist: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" style={{ ...styles.btn, ...styles.primaryBtn }} disabled={loading}>
                  {loading ? "Creating..." : "Create App"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && newCredentials && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🔑 API Credentials</h3>
            <div style={styles.credentialBox}>
              <strong>⚠️ Important:</strong> Save these credentials now. The API Secret will not be shown again!
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>API Key</label>
              <div style={{ display: "flex", gap: "10px" }}>
                <input type="text" style={{ ...styles.input, flex: 1 }} value={newCredentials.apiKey} readOnly />
                <button style={{ ...styles.btn, ...styles.secondaryBtn, flex: "none", width: "80px" }} onClick={() => copyToClipboard(newCredentials.apiKey)}>
                  Copy
                </button>
              </div>
            </div>

            {newCredentials.apiSecret && (
              <div style={styles.formGroup}>
                <label style={styles.label}>API Secret</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input type="text" style={{ ...styles.input, flex: 1, background: "#fef3c7" }} value={newCredentials.apiSecret} readOnly />
                  <button style={{ ...styles.btn, ...styles.secondaryBtn, flex: "none", width: "80px" }} onClick={() => copyToClipboard(newCredentials.apiSecret)}>
                    Copy
                  </button>
                </div>
              </div>
            )}

            {newCredentials.webhookSecret && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Webhook Secret</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input type="text" style={{ ...styles.input, flex: 1 }} value={newCredentials.webhookSecret} readOnly />
                  <button style={{ ...styles.btn, ...styles.secondaryBtn, flex: "none", width: "80px" }} onClick={() => copyToClipboard(newCredentials.webhookSecret)}>
                    Copy
                  </button>
                </div>
              </div>
            )}

            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={() => setShowCredentialsModal(false)}>
                I've Saved My Credentials
              </button>
            </div>
          </div>
        </div>
      )}

      {/* App Details Modal */}
      {showDetailsModal && selectedApp && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, ...styles.modalLarge }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ ...styles.modalTitle, marginBottom: 0 }}>📱 {selectedApp.appName}</h3>
              <span style={{ ...styles.badge, ...(selectedApp.status === "Active" ? styles.activeBadge : styles.revokedBadge) }}>
                {selectedApp.status}
              </span>
            </div>

            {appStats && (
              <div style={styles.statsRow}>
                <div style={styles.stat}>
                  <div style={styles.statValue}>{appStats.totalRequests.toLocaleString()}</div>
                  <div style={styles.statLabel}>Total Requests</div>
                </div>
                <div style={styles.stat}>
                  <div style={{ ...styles.statValue, color: "#16a34a" }}>{appStats.successRate.toFixed(1)}%</div>
                  <div style={styles.statLabel}>Success Rate</div>
                </div>
                <div style={styles.stat}>
                  <div style={styles.statValue}>{appStats.requestsToday}</div>
                  <div style={styles.statLabel}>Requests Today</div>
                </div>
                <div style={styles.stat}>
                  <div style={styles.statValue}>{appStats.requestsThisMinute}/{appStats.rateLimitPerMinute}</div>
                  <div style={styles.statLabel}>Rate (per min)</div>
                </div>
              </div>
            )}

            <div style={{ marginTop: "20px" }}>
              <h4>API Credentials</h4>
              <div style={{ ...styles.apiKey, marginTop: "10px" }}>
                <strong>API Key:</strong> {selectedApp.apiKey}
              </div>
            </div>

            <div style={{ marginTop: "20px" }}>
              <h4>Subscribed Webhook Events</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "10px" }}>
                {selectedApp.subscribedEvents?.length > 0 ? (
                  selectedApp.subscribedEvents.map(e => (
                    <span key={e} style={{ ...styles.badge, background: "#fef3c7", color: "#92400e" }}>{e}</span>
                  ))
                ) : (
                  <span style={{ color: "#6b7280" }}>No webhook subscriptions</span>
                )}
              </div>
            </div>

            {appLogs.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <h4>Recent API Requests</h4>
                <div style={{ maxHeight: "200px", overflow: "auto", marginTop: "10px" }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Time</th>
                        <th style={styles.th}>Method</th>
                        <th style={styles.th}>Endpoint</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Time (ms)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appLogs.map(log => (
                        <tr key={log.id}>
                          <td style={styles.td}>{formatDate(log.createdAt)}</td>
                          <td style={styles.td}><span style={{ ...styles.badge, background: "#e0e7ff", color: "#4f46e5" }}>{log.httpMethod}</span></td>
                          <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "11px" }}>{log.endpoint}</td>
                          <td style={styles.td}>
                            <span style={{ 
                              ...styles.badge, 
                              ...(log.responseStatusCode < 400 ? styles.activeBadge : styles.revokedBadge) 
                            }}>
                              {log.responseStatusCode}
                            </span>
                          </td>
                          <td style={styles.td}>{log.responseTimeMs}ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div style={{ ...styles.btnRow, marginTop: "20px" }}>
              <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => handleTestWebhook(selectedApp.id, "transaction.created")}>
                🧪 Test Webhook
              </button>
              <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={() => handleRegenerateKeys(selectedApp.id)}>
                🔄 Regenerate Keys
              </button>
              {selectedApp.status === "Active" && (
                <button style={{ ...styles.btn, ...styles.dangerBtn }} onClick={() => handleRevokeApp(selectedApp.id, selectedApp.appName)}>
                  ❌ Revoke App
                </button>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

